import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronRight } from 'lucide-react';
import { SmartTable } from '../../SmartTable';
import { ExportMenu } from '../../ExportMenu';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { CrashDetailsModal } from '../../shared/CrashDetailsModal';
import { type VirtualTableColumn } from '../../VirtualTable';
import type {
  CarrierProfile as CarrierProfileType,
  Crash,
} from '../../../types/api';

export interface CrashesProps {
  profile: CarrierProfileType;
  crashes: Crash[];
  loadingMore: { [key: string]: boolean };
  onLoadMoreCrashes: () => void;
  setSelectedVin: (vin: string | null) => void;
}

const Crashes: React.FC<CrashesProps> = ({
  profile,
  crashes,
  loadingMore,
  onLoadMoreCrashes,
  setSelectedVin,
}) => {
  const [selectedCrash, setSelectedCrash] = useState<any | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Export Buttons */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
        <ExportMenu
          dataType="crashes"
          data={crashes}
          carrierName={profile?.legalName}
          dotNumber={profile?.dotNumber}
          buttonText="Export Crashes"
          size="sm"
        />
      </div>

      {/* Info Banner */}
      <div className="card-modern fade-in-up" style={{ borderLeft: '4px solid var(--color-danger)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
          <AlertTriangle style={{ width: 24, height: 24, color: 'var(--color-danger)', flexShrink: 0 }} />
          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
              {crashes.length} Crash Incidents
            </h4>
            <p className="text-muted">
              Complete crash history with VINs. Click any VIN to track shell game fraud across carriers.
            </p>
          </div>
        </div>
      </div>

      {crashes.length > 0 ? (
        <>
          <div className="card-modern fade-in-up" style={{ animationDelay: '0.1s', padding: 0, overflow: 'hidden' }}>
            <SmartTable
              data={crashes}
              columns={[
                {
                  key: 'reportNumber',
                  header: 'Report #',
                  width: 120,
                  sortable: true,
                  render: (crash) => {
                    const reportNum = (crash as Crash & { reportNumber?: string }).reportNumber || (crash as any).report_number;
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedCrash(crash);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#34312c',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontFamily: 'monospace',
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          padding: 0
                        }}
                      >
                        {reportNum || 'N/A'}
                      </button>
                    );
                  }
                },
                {
                  key: 'reportDate',
                  header: 'Date',
                  width: 100,
                  sortable: true,
                  className: 'monospace',
                  render: (crash) => new Date((crash as Crash & { reportDate?: string }).reportDate || crash.reportDate || '').toLocaleDateString()
                },
                {
                  key: 'reportState',
                  header: 'State',
                  width: 60,
                  sortable: true,
                  className: 'bold'
                },
                {
                  key: 'location',
                  header: 'Location',
                  width: 200,
                  render: (crash) => {
                    const extCrash = crash as Crash & { location?: string; roadway?: string; reportState?: string; crashCarrierState?: string };
                    const loc = extCrash.location || extCrash.roadway || extCrash.city || '';
                    const city = extCrash.city || '';
                    const state = extCrash.reportState || extCrash.crashCarrierState || extCrash.state || '';
                    if (loc) {
                      return (
                        <div style={{ fontSize: '0.875rem' }}>
                          <div>{loc}</div>
                          {city && city !== loc && <div style={{ color: 'var(--color-gray-500)', fontSize: '0.75rem' }}>{city}, {state}</div>}
                        </div>
                      );
                    }
                    return city ? `${city}, ${state}` : (state || 'N/A');
                  }
                },
                {
                  key: 'vehicleIdentificationNumber',
                  header: 'VIN',
                  width: 140,
                  render: (crash) => {
                    const vin = (crash as Crash & { vehicleIdentificationNumber?: string }).vehicleIdentificationNumber;
                    return vin ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedVin(vin);
                        }}
                        style={{
                          background: '#efebe1',
                          border: '1px solid var(--color-primary)',
                          borderRadius: '4px',
                          color: 'var(--color-primary)',
                          cursor: 'pointer',
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          padding: '4px 8px'
                        }}
                      >
                        {vin.slice(-8)}
                      </button>
                    ) : <span style={{ color: 'var(--color-gray-400)' }}>N/A</span>;
                  }
                },
                {
                  key: 'weather',
                  header: 'Weather',
                  width: 120,
                  render: (crash) => {
                    const extCrash = crash as Crash & { weatherConditionId?: number; lightConditionId?: number; roadSurfaceConditionId?: number };
                    const weather = crash.weather_condition_id || extCrash.weatherConditionId;
                    const light = crash.light_condition_id || extCrash.lightConditionId;
                    const road = crash.road_surface_condition_id || extCrash.roadSurfaceConditionId;
                    if (!weather && !light && !road) {
                      return <span style={{ color: 'var(--color-gray-400)' }}>-</span>;
                    }
                    return (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>
                        {weather && <div>{weather}</div>}
                        {light && <div>{light}</div>}
                        {road && <div>{road}</div>}
                      </div>
                    );
                  }
                },
                {
                  key: 'severity',
                  header: 'Severity',
                  width: 90,
                  render: (crash) => {
                    const extCrash = crash as Crash & { towAway?: string };
                    return crash.fatalities > 0 ? (
                      <span className="badge-modern badge-danger">Fatal</span>
                    ) : crash.injuries > 0 ? (
                      <span className="badge-modern badge-warning">Injury</span>
                    ) : (extCrash.towAway || crash.towAway) === 'Y' ? (
                      <span className="badge-modern badge-neutral">Tow-Away</span>
                    ) : (
                      <span className="badge-modern badge-success">Property</span>
                    );
                  }
                },
                {
                  key: 'isPreventable',
                  header: 'Preventable',
                  width: 100,
                  render: (crash) => {
                    const isPreventable = (crash as any).is_preventable ?? (crash as any).isPreventable;
                    if (isPreventable === null || isPreventable === undefined) {
                      return <span style={{ color: '#857f73', fontSize: '0.75rem' }}>N/A</span>;
                    }
                    return isPreventable === false ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: '#d1e6d9',
                          color: '#4b5c3a',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        No
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: '#f2dad2',
                          color: '#7a2b1e',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        Yes
                      </span>
                    );
                  }
                },
                {
                  key: 'details',
                  header: 'Details',
                  render: (crash) => (
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                      {crash.fatalities > 0 && <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>{crash.fatalities} Fatal, </span>}
                      {crash.injuries > 0 && <span>{crash.injuries} Injured, </span>}
                      {(crash as Crash & { hazmatReleased?: string }).hazmatReleased === 'Y' && <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>Hazmat Released</span>}
                    </span>
                  )
                }
              ] as VirtualTableColumn<Crash>[]}
              keyExtractor={(crash, idx) => (crash as Crash & { crashId?: string }).crashId || crash.crash_id || idx.toString()}
              rowHeight={60}
              height="auto"
              virtualThreshold={100}
              sortable={true}
              stickyHeader={true}
              getRowClassName={(crash) =>
                crash.fatalities > 0 ? 'danger' : crash.injuries > 0 ? 'warning' : ''
              }
              emptyMessage="No crash records found"
            />
          </div>

        {/* Load More Button for Crashes */}
        <button
          onClick={() => onLoadMoreCrashes()}
          disabled={loadingMore['crashes']}
          className="btn-secondary"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)',
            marginTop: 'var(--space-lg)'
          }}
        >
          {loadingMore['crashes'] ? (
            <>
              <LoadingSpinner size="sm" />
              Loading more...
            </>
          ) : (
            <>
              <ChevronRight size={18} />
              Load More Crashes (now showing {crashes.length})
            </>
          )}
        </button>
        </>
      ) : (
        <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
          <CheckCircle style={{ width: 40, height: 40, color: 'var(--color-success)', margin: '0 auto var(--space-lg)' }} />
          <p className="text-muted" style={{ fontSize: '1rem' }}>No crash records found for this carrier.</p>
        </div>
      )}

      {/* Crash Details Modal */}
      <CrashDetailsModal crash={selectedCrash} onClose={() => setSelectedCrash(null)} />
    </div>
  );
};

export default Crashes;
